// Голямата търсачка на работен лист: текстът, маркираният резултат,
// видимостта на падащия списък и клавишите ѝ — едно копие за четирите листа.
//
//   ↓/↑    — маркират резултат; ↓ без резултати → първият ред на решетката
//   Enter  — взема маркирания резултат; с празен текст → първият ред
//   Esc    — на три стъпки: затваря списъка → чисти текста → излиза от полето
//
// Резултатите се смятат от листа (зависят от текста, който е ТУК), затова
// hook-ът ги чете през функции в момента на натискане, не като масив.
import { useCallback, useMemo, useRef, useState } from "react";

type Params = {
  // брой резултати в момента на натискане
  getResultCount: () => number;
  // взема резултата на позиция `index` (листът решава: нов ред, фокус в
  // съществуващ, разкриване на ред…)
  onPickIndex: (index: number) => void;
  // ↓/Enter към първия ред на решетката; false = няма редове
  focusFirst: () => boolean;
};

export const useWorksheetSearch = ({
  getResultCount,
  onPickIndex,
  focusFirst,
}: Params) => {
  const [search, setSearchState] = useState("");
  const [focused, setFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  // Esc на стъпки: първото затваря списъка, второто чисти търсачката
  const [dropdownSuppressed, setDropdownSuppressed] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Текстът синхронно (машината за писане дописва преди React да пребоядиса)
  const searchTextRef = useRef("");

  const paramsRef = useRef({ getResultCount, onPickIndex, focusFirst });
  paramsRef.current = { getResultCount, onPickIndex, focusFirst };

  // Всяка смяна на текста: списъкът се показва отново, маркирането — от 0
  const setSearch = useCallback((value: string) => {
    searchTextRef.current = value;
    setSearchState(value);
    setDropdownSuppressed(false);
    setHighlightedIndex(0);
  }, []);

  const focusSearch = useCallback(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const searchWords = useMemo(
    () => search.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [search],
  );

  const dropdownOpen = focused && search.trim() !== "" && !dropdownSuppressed;

  const onInputKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const { getResultCount, onPickIndex, focusFirst } = paramsRef.current;
      const count = getResultCount();
      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (count) setHighlightedIndex((i) => Math.min(i + 1, count - 1));
        else focusFirst();
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (event.key === "Enter") {
        // Ctrl+Enter е записът на листа — не е „вземи резултат"
        if (event.ctrlKey || event.metaKey) return;
        event.preventDefault();
        if (count) onPickIndex(Math.min(highlightedIndex, count - 1));
        else if (!searchTextRef.current.trim()) focusFirst();
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        if (dropdownOpen) setDropdownSuppressed(true);
        else if (searchTextRef.current) setSearch("");
        else inputRef.current?.blur();
      }
    },
    [dropdownOpen, highlightedIndex, setSearch],
  );

  return {
    search,
    setSearch,
    searchTextRef,
    searchWords,
    inputRef,
    focusSearch,
    focused,
    setFocused,
    highlightedIndex,
    setHighlightedIndex,
    dropdownOpen,
    suppressDropdown: () => setDropdownSuppressed(true),
    onInputKeyDown,
  };
};
